import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Tag, Store, Package, Calendar, EuroIcon, AlertTriangle } from 'lucide-react';
import { useAppStore } from '../../shared/store';
import { generateId } from '../../shared/utils/id';
import { Deal } from '../../shared/types';
import { toast } from '../../shared/components/Toast';
interface DealDialogProps {
  isOpen: boolean;
  onClose: () => void;
  dealToEdit?: Deal | null;
}
export function DealDialog({
  isOpen,
  onClose,
  dealToEdit
}: DealDialogProps) {
  const {
    inventory,
    stores,
    addDeal,
    updateDeal
  } = useAppStore();
  const isAddMode = !dealToEdit;
  const [formData, setFormData] = useState({
    itemId: '',
    retailerId: '',
    originalPrice: '',
    discountedPrice: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    type: 'promotion' as 'bulk' | 'promotion' | 'seasonal',
    minQuantity: '',
    maxQuantity: '',
    isActive: true
  });
  const [error, setError] = useState('');
  useEffect(() => {
    if (dealToEdit) {
      setFormData({
        itemId: dealToEdit.itemId,
        retailerId: dealToEdit.retailerId,
        originalPrice: dealToEdit.originalPrice.toString(),
        discountedPrice: dealToEdit.discountedPrice.toString(),
        startDate: dealToEdit.startDate.split('T')[0],
        endDate: dealToEdit.endDate.split('T')[0],
        type: dealToEdit.type,
        minQuantity: dealToEdit.minQuantity?.toString() || '',
        maxQuantity: dealToEdit.maxQuantity?.toString() || '',
        isActive: dealToEdit.isActive
      });
    } else {
      setFormData({
        itemId: '',
        retailerId: '',
        originalPrice: '',
        discountedPrice: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        type: 'promotion',
        minQuantity: '',
        maxQuantity: '',
        isActive: true
      });
    }
    setError('');
  }, [dealToEdit, isOpen]);
  const handleItemChange = (itemId: string) => {
    const item = inventory.find(i => i.id === itemId);
    if (item) {
      setFormData(prev => ({
        ...prev,
        itemId,
        originalPrice: item.price.toString()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        itemId
      }));
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.itemId || !formData.retailerId || !formData.originalPrice || !formData.discountedPrice) {
      setError('Vul alle verplichte velden in');
      return;
    }
    const origPrice = parseFloat(formData.originalPrice);
    const discPrice = parseFloat(formData.discountedPrice);
    const startDate = new Date(`${formData.startDate}T00:00:00`);
    const endDate = new Date(`${formData.endDate}T23:59:59`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedStore = stores.find(store => store.id === formData.retailerId);
    if (!Number.isFinite(origPrice) || !Number.isFinite(discPrice) || origPrice <= 0 || discPrice < 0 || discPrice >= origPrice) {
      setError('Vul een geldige kortingsprijs in die lager is dan de originele prijs');
      return;
    }
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      setError('Vul een geldige start- en einddatum in');
      return;
    }
    if (endDate < startDate) {
      setError('Einddatum moet op of na de startdatum liggen');
      return;
    }
    if (endDate < today) {
      setError('Einddatum moet vandaag of later zijn');
      return;
    }
    if (!selectedStore?.isActive) {
      setError('Selecteer een actieve winkel voor deze aanbieding');
      return;
    }
    const dealData: Deal = {
      id: isAddMode ? generateId() : dealToEdit!.id,
      itemId: formData.itemId,
      retailerId: formData.retailerId,
      originalPrice: origPrice,
      discountedPrice: discPrice,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      type: formData.type,
      minQuantity: formData.minQuantity ? parseInt(formData.minQuantity) : undefined,
      maxQuantity: formData.maxQuantity ? parseInt(formData.maxQuantity) : undefined,
      isActive: formData.isActive,
      usageCount: isAddMode ? 0 : dealToEdit!.usageCount,
      totalSavings: isAddMode ? 0 : dealToEdit!.totalSavings
    };
    if (isAddMode) {
      addDeal(dealData);
      toast.success('Aanbieding toegevoegd');
    } else {
      updateDeal(dealData.id, dealData);
      toast.success('Aanbieding bijgewerkt');
    }
    onClose();
  };
  return <AnimatePresence data-id="element-1474" data-mp="20intyzopmwoj">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm sm:p-4" onClick={onClose} data-id="element-1475" data-mp="25bh3s1z1cv7h">
          <motion.div initial={{
        y: '100%',
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: '100%',
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} onClick={e => e.stopPropagation()} className="w-full max-w-lg bg-card rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-1476" data-mp="2ohoknmw41uyt">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0" data-id="element-1477" data-mp="3h6cazwvpvaj8">
              <div className="flex items-center gap-3" data-id="element-1478" data-mp="3k64ann7jywag">
                <div className="p-2 bg-primary/10 rounded-xl" data-id="element-1479" data-mp="3k5ksc5dzq5vw">
                  <Tag className="w-5 h-5 text-primary" data-id="element-1480" data-mp="3o76b27d7hee0" />
                </div>
                <h2 className="text-lg font-semibold text-foreground" data-id="element-1481" data-mp="3t6sp2j0uyynv">
                  {isAddMode ? 'Nieuwe Aanbieding' : 'Aanbieding Bewerken'}
                </h2>
              </div>
              <button onClick={onClose} className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-colors" data-id="element-1482" data-mp="1y53q32mbbgb">
                <X className="w-5 h-5" data-id="element-1483" data-mp="35f538figedsg" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1" data-id="element-1484" data-mp="3aezx8rxizjhq">
              <form id="deal-form" onSubmit={handleSubmit} className="space-y-5" data-id="element-1485" data-mp="3f7ax5us9n5aa">
                {error && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm flex items-center gap-2" data-id="element-1486" data-mp="2mqqfq27chhwe">
                    <AlertTriangle className="w-4 h-4 shrink-0" data-id="element-1487" data-mp="2f5weir3wsed6" />
                    {error}
                  </div>}

                <div className="space-y-4" data-id="element-1488" data-mp="2a69zqd8fmq29">
                  <div data-id="element-1489" data-mp="2588djjfpmamk">
                    <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-1490" data-mp="257glgilbmv11">
                      Product *
                    </label>
                    <div className="relative" data-id="element-1491" data-mp="1w83a1kdmzwdo">
                      <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" data-id="element-1492" data-mp="1w9whh1w2qkmq" />
                      <select value={formData.itemId} onChange={e => handleItemChange(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-muted/50 border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground appearance-none" required data-id="element-1493" data-mp="1rfjvak37qv7a">
                        <option value="" data-id="element-1494" data-mp="18k284uayre78">Selecteer een product</option>
                        {inventory.map(item => <option key={item.id} value={item.id} data-id="element-1495" data-mp="2wddqzjwp1sja">
                            {item.name}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div data-id="element-1496" data-mp="2rfknwzqoten8">
                    <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-1497" data-mp="2rf2gd6ykre00">
                      Winkel *
                    </label>
                    <div className="relative" data-id="element-1498" data-mp="2ifp8giq3mxhe">
                      <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" data-id="element-1499" data-mp="2igh2hp41sn5k" />
                      <select value={formData.retailerId} onChange={e => setFormData({
                    ...formData,
                    retailerId: e.target.value
                  })} className="w-full pl-10 pr-4 py-3 bg-muted/50 border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground appearance-none" required data-id="element-1500" data-mp="23scgab5sdti1">
                        <option value="" data-id="element-1501" data-mp="1p3ucv1sb3a7c">Selecteer een actieve winkel</option>
                        {stores.filter(store => store.isActive).map(store => <option key={store.id} value={store.id} data-id="element-1502" data-mp="1z1guhw0zpfv0">
                              {store.name}
                            </option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-1503" data-mp="27wog4ygkhdvq">
                    <div data-id="element-1504" data-mp="23u1mcs0c5ivw">
                      <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-1505" data-mp="23ts4xstcwk1t">
                        Originele Prijs *
                      </label>
                      <div className="relative" data-id="element-1506" data-mp="2cugby3kc1k57">
                        <EuroIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" data-id="element-1507" data-mp="2crke0f8hivgr" />
                        <input type="number" step="0.01" min="0" value={formData.originalPrice} onChange={e => setFormData({
                      ...formData,
                      originalPrice: e.target.value
                    })} className="w-full pl-10 pr-4 py-3 bg-muted/50 border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" placeholder="0.00" required data-id="element-1508" data-mp="197s6a4qmwyeh" />
                      </div>
                    </div>
                    <div data-id="element-1509" data-mp="cj2nmc76o1rg">
                      <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-1510" data-mp="cf577ybzpob9">
                        Actieprijs *
                      </label>
                      <div className="relative" data-id="element-1511" data-mp="3tl6w2usdjegz">
                        <EuroIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" data-id="element-1512" data-mp="2dkq2vi6mzwy" />
                        <input type="number" step="0.01" min="0" value={formData.discountedPrice} onChange={e => setFormData({
                      ...formData,
                      discountedPrice: e.target.value
                    })} className="w-full pl-10 pr-4 py-3 bg-primary/5 border border-primary/20 rounded-xl focus:ring-2 focus:ring-primary text-foreground" placeholder="0.00" required data-id="element-1513" data-mp="7cxj5xznd37z" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-1514" data-mp="2osnmbnjr8g3p">
                    <div data-id="element-1515" data-mp="2ot5rxasb46oa">
                      <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-1516" data-mp="2jtu6vpkwqf0w">
                        Start Datum *
                      </label>
                      <div className="relative" data-id="element-1517" data-mp="2yso95jgu1ecn">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" data-id="element-1518" data-mp="2ts9yrurrdsbg" />
                        <input type="date" value={formData.startDate} onChange={e => setFormData({
                      ...formData,
                      startDate: e.target.value
                    })} className="w-full pl-10 pr-4 py-3 bg-muted/50 border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" required data-id="element-1519" data-mp="37qzwncmejw0v" />
                      </div>
                    </div>
                    <div data-id="element-1520" data-mp="903klwqdsvhs">
                      <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-1521" data-mp="8xr7pxzhdbbl">
                        Eind Datum *
                      </label>
                      <div className="relative" data-id="element-1522" data-mp="3w3i4m6li287a">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" data-id="element-1523" data-mp="4xyvnbavon1h" />
                        <input type="date" value={formData.endDate} onChange={e => setFormData({
                      ...formData,
                      endDate: e.target.value
                    })} className="w-full pl-10 pr-4 py-3 bg-muted/50 border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" required data-id="element-1524" data-mp="3mdym521p8gcy" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-1525" data-mp="elxvb37lsqfd">
                    <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-1526" data-mp="al48ye9t7jol">
                      Type Aanbieding
                    </label>
                    <select value={formData.type} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value as any
                })} className="w-full px-4 py-3 bg-muted/50 border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground appearance-none" data-id="element-1527" data-mp="5t4civsy78wr">
                      <option value="promotion" data-id="element-1528" data-mp="3e2mh6mthuspj">Standaard Korting</option>
                      <option value="bulk" data-id="element-1529" data-mp="1h9koug97h9fe">Bulk / Meer-stuks</option>
                      <option value="seasonal" data-id="element-1530" data-mp="1h8sz3m2ai5x1">Seizoensgebonden</option>
                    </select>
                  </div>

                  {formData.type === 'bulk' && <div className="grid grid-cols-2 gap-4" data-id="element-1531" data-mp="1jt7jtwq5dlu2">
                      <div data-id="element-1532" data-mp="1ostsrtyy0fx2">
                        <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-1533" data-mp="1os228xk7d8dn">
                          Min. Aantal
                        </label>
                        <input type="number" min="1" value={formData.minQuantity} onChange={e => setFormData({
                    ...formData,
                    minQuantity: e.target.value
                  })} className="w-full px-4 py-3 bg-muted/50 border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" placeholder="Bijv. 3" data-id="element-1534" data-mp="1yin7v4yand62" />
                      </div>
                      <div data-id="element-1535" data-mp="2q8hwezebuyto">
                        <label className="block text-sm font-medium text-foreground mb-1.5" data-id="element-1536" data-mp="2qbxemb3asnzb">
                          Max. Aantal
                        </label>
                        <input type="number" min="1" value={formData.maxQuantity} onChange={e => setFormData({
                    ...formData,
                    maxQuantity: e.target.value
                  })} className="w-full px-4 py-3 bg-muted/50 border border-border rounded-xl focus:ring-2 focus:ring-primary text-foreground" placeholder="Optioneel" data-id="element-1537" data-mp="m5q3663keyk5" />
                      </div>
                    </div>}

                  <div className="flex items-center gap-3 pt-2" data-id="element-1538" data-mp="3kunhld4cfuhk">
                    <button type="button" onClick={() => setFormData({
                  ...formData,
                  isActive: !formData.isActive
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.isActive ? 'bg-primary' : 'bg-muted'}`} data-id="element-1539" data-mp="3fxx3uwg0hazb">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.isActive ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1540" data-mp="1rb55lhz3pvt6" />
                    </button>
                    <span className="text-sm font-medium text-foreground" data-id="element-1541" data-mp="253usdomgf4rc">
                      Aanbieding is actief
                    </span>
                  </div>
                </div>
              </form>
            </div>

            <div className="p-6 border-t border-border bg-muted/30 flex gap-3 shrink-0" data-id="element-1542" data-mp="tofu8hiie6un">
              <button type="button" onClick={onClose} className="flex-1 py-3 bg-muted hover:bg-muted/80 text-foreground font-medium rounded-xl transition-colors" data-id="element-1543" data-mp="yqex7qac71r2">
                Annuleren
              </button>
              <button type="submit" form="deal-form" className="flex-1 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-xl transition-colors shadow-sm" data-id="element-1544" data-mp="1gfab77dxea1e">
                {isAddMode ? 'Toevoegen' : 'Opslaan'}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}